import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

/**
 * Confirms removing a user from an organization. The available outcomes depend on
 * the user's other memberships and on whether they are the last member of this org:
 *
 *  - other users remain in this org  -> detach, or delete the user; the org is unaffected
 *  - last user in this org           -> caller chooses whether the org survives
 *
 * Being the last member is what matters, not how the user leaves: a detach empties the
 * organization exactly as a delete does. Both paths are gated server-side behind
 * require_move_and_delete_organization, so the last-user case always asks, sending
 * keep_organization to spare the org or move_and_delete_organization to remove it.
 */
const RemoveUserDialog = ({
  user,
  organizationName,
  isDetach,
  isLastUser,
  onCancel,
  onDetach,
  onDetachKeepOrganization,
  onDetachWithOrganization,
  onDelete,
  onDeleteKeepOrganization,
  onDeleteWithOrganization,
}) => {
  if (!user) return null;

  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'this user';
  const org = organizationName || 'this organization';
  const leaving = isDetach ? 'Removing them' : 'Deleting them';

  const title = () => {
    if (isLastUser) return 'Last User In Organization';
    return isDetach ? 'Remove From Organization' : 'Delete User';
  };

  const message = () => {
    if (isLastUser) {
      return `${name} is the last user in ${org}. ${leaving} leaves the organization with no users — choose whether to keep it.`;
    }
    if (isDetach) {
      return `Remove ${name} from ${org}? Their account and their access to other organizations are not affected.`;
    }
    return `Delete ${name}? This permanently removes their account.`;
  };

  const keepLabel = isDetach ? 'Remove User, Keep Organization' : 'Delete User, Keep Organization';
  const withOrgLabel = isDetach ? 'Remove User And Delete Organization' : 'Delete User And Organization';

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      aria-labelledby="remove-user-dialog-title"
      aria-describedby="remove-user-dialog-description"
    >
      <DialogTitle id="remove-user-dialog-title">{title()}</DialogTitle>
      <DialogContent>
        <DialogContentText id="remove-user-dialog-description">
          {message()}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        {isLastUser ? (
          <>
            <Button
              onClick={isDetach ? onDetachKeepOrganization : onDeleteKeepOrganization}
              color="primary"
              variant="contained"
            >
              {keepLabel}
            </Button>
            <Button
              onClick={isDetach ? onDetachWithOrganization : onDeleteWithOrganization}
              color="warning"
              variant="contained"
            >
              {withOrgLabel}
            </Button>
          </>
        ) : (
          <Button onClick={isDetach ? onDetach : onDelete} color="warning" variant="contained">
            {isDetach ? 'Remove From Organization' : 'Delete User'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RemoveUserDialog;
